import React, { useMemo } from "react";
import {
  useListUsers,
  useUpdateUserRole,
  getListUsersQueryKey,
  type UserRole,
  type CurrentUser,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { Check, Minus, Info } from "lucide-react";

const ROLE_ORDER: UserRole[] = [
  "agent",
  "master_dealer",
  "account_manager",
  "admin",
  "finance",
  "poc_support",
  "operations",
];

const ROLE_LABELS: Record<UserRole, string> = {
  agent: "Agent",
  master_dealer: "Master Dealer",
  account_manager: "Account Manager",
  admin: "Admin",
  finance: "Finance",
  support: "Support",
  poc_support: "PoC Support",
  operations: "Operations",
};

// Reference matrix shown to admins. The server's path-scoped role guards are the
// source of truth; this mirrors them so an admin can review who can do what.
const CAPABILITIES: { label: string; roles: UserRole[] }[] = [
  { label: "Manage users & roles", roles: ["admin"] },
  {
    label: "Send invitations",
    roles: ["master_dealer", "account_manager", "admin"],
  },
  {
    label: "Review applications",
    roles: ["master_dealer", "account_manager", "admin", "operations"],
  },
  {
    label: "Manage agents & network",
    roles: ["master_dealer", "account_manager", "admin"],
  },
  {
    label: "View orders",
    roles: ["agent", "master_dealer", "account_manager", "admin"],
  },
  { label: "Wallet & transactions", roles: ["agent", "finance", "admin"] },
  { label: "Payment runs & payouts", roles: ["finance", "admin"] },
  { label: "Bank change approvals", roles: ["agent", "finance", "admin"] },
  {
    label: "Tickets",
    roles: ["agent", "poc_support", "account_manager", "admin"],
  },
  { label: "Disputes", roles: ["agent", "poc_support", "finance", "admin"] },
  {
    label: "Data imports",
    roles: ["admin", "finance"],
  },
  { label: "Audit log", roles: ["admin"] },
];

export function UsersRolesList() {
  const { data: users, isLoading, error } = useListUsers();

  const adminCount = useMemo(
    () => (users ?? []).filter((u) => u.role === "admin").length,
    [users],
  );

  if (isLoading)
    return (
      <div className="p-6 animate-pulse">
        <div className="h-8 bg-gray-200 w-1/4 rounded mb-6"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  if (error || !users)
    return <div className="p-6 text-red-500">Failed to load users.</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#0B132B]">Users &amp; Roles</h1>
        <p className="text-sm text-gray-500 mt-1">
          Assign roles to portal users. Changes take effect immediately and are
          recorded in the audit log.
        </p>
      </div>

      {adminCount <= 1 && (
        <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#0B132B]" />
          <span>
            There is only one admin. Assign another admin before changing this
            one — the last admin cannot be demoted.
          </span>
        </div>
      )}

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#0B132B]">
            All users ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="w-[200px]">Role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8 border border-gray-200">
                        <AvatarImage src={user.avatarUrl ?? undefined} />
                        <AvatarFallback className="bg-[#0B132B] text-white text-xs">
                          {user.fullName?.charAt(0)?.toUpperCase() || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{user.fullName}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-600">{user.email}</TableCell>
                  <TableCell>
                    <RoleCell user={user} adminCount={adminCount} />
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="text-center p-8 text-gray-500"
                  >
                    No users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#0B132B]">
            Roles &amp; rights
          </CardTitle>
          <p className="text-sm text-gray-500">
            What each role can access. The server enforces these rules; this
            table is a reference.
          </p>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="min-w-[200px]">Capability</TableHead>
                {ROLE_ORDER.map((r) => (
                  <TableHead key={r} className="text-center whitespace-nowrap">
                    {ROLE_LABELS[r]}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {CAPABILITIES.map((cap) => (
                <TableRow key={cap.label}>
                  <TableCell className="font-medium">{cap.label}</TableCell>
                  {ROLE_ORDER.map((r) => (
                    <TableCell key={r} className="text-center">
                      {cap.roles.includes(r) ? (
                        <Check className="w-4 h-4 mx-auto text-[#00A859]" />
                      ) : (
                        <Minus className="w-4 h-4 mx-auto text-gray-300" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function RoleCell({
  user,
  adminCount,
}: {
  user: CurrentUser;
  adminCount: number;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { mutate, isPending } = useUpdateUserRole();

  const isLastAdmin = user.role === "admin" && adminCount <= 1;

  const handleChange = (next: string) => {
    if (next === user.role) return;
    if (isLastAdmin && next !== "admin") {
      toast({
        title: "Cannot remove the last admin",
        description: "Assign another admin before changing this one.",
        variant: "destructive",
      });
      return;
    }
    mutate(
      { id: user.id, data: { role: next as UserRole } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListUsersQueryKey() });
          toast({
            title: "Role updated",
            description: `${user.fullName} is now ${ROLE_LABELS[next as UserRole]}.`,
          });
        },
        onError: () => {
          toast({
            title: "Could not update role",
            description: "Please try again.",
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <Select value={user.role} onValueChange={handleChange} disabled={isPending}>
      <SelectTrigger className="h-9 w-[180px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {ROLE_ORDER.map((r) => (
          <SelectItem key={r} value={r}>
            {ROLE_LABELS[r]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
